// Pure helpers over the store registry (spec 2.1, 4.1, 4.3).

export interface PosStore {
  /** Row id (pos_stores.id). Optional only for old call sites/fixtures built
   *  before store management existed; every live read includes it. */
  id?: number;
  brand: string;
  store_id: string;
  location_name: string;
  available: boolean;
}

export interface BrandAggregate {
  brand: string;
  total: number;
  available: number;
}

/**
 * The /api/brands aggregate: one row per brand with its total and available
 * counts, sorted case-insensitively by brand name (spec section 3, 4.1).
 */
export function aggregateBrands(stores: readonly PosStore[]): BrandAggregate[] {
  const byBrand = new Map<string, BrandAggregate>();
  for (const s of stores) {
    const agg = byBrand.get(s.brand) ?? { brand: s.brand, total: 0, available: 0 };
    agg.total += 1;
    if (s.available) agg.available += 1;
    byBrand.set(s.brand, agg);
  }
  return [...byBrand.values()].sort((a, b) =>
    a.brand.toLowerCase().localeCompare(b.brand.toLowerCase()),
  );
}

/**
 * Case-insensitive substring match on location_name, optionally narrowed to one
 * brand, capped at `limit` (spec 4.1). An empty query returns nothing: 331
 * stores are far too many to dump, so the picker says "Start typing to search".
 */
export function filterStores(
  stores: readonly PosStore[],
  query: string,
  brand: string,
  limit = 40,
): PosStore[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const out: PosStore[] = [];
  for (const s of stores) {
    if (brand && s.brand !== brand) continue;
    if (!s.location_name.toLowerCase().includes(q)) continue;
    out.push(s);
    if (out.length >= limit) break;
  }
  return out;
}

/** One of the eight `brand-swatch-*` Tailwind/CSS tokens (DESIGN.md > Colors >
 *  Brand swatches). 1-3 are pinned to the three onboarded brands; 4 is the
 *  neutral fallback for a blank brand; 5-8 are hashed onto any other brand so
 *  a brand added through the app still gets a stable, distinct colour instead
 *  of collapsing onto the shared gray. */
export type BrandSwatch = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;

/** Known brands -> swatch. Add a new brand here when it's formally onboarded
 *  (its own hue, not a hash slot); until then it hashes into 5-8. */
export const BRAND_SWATCHES: Readonly<Record<string, BrandSwatch>> = {
  "Mountain Mike's Pizza": 1,
  TandooriPizza: 2,
  PizzaMyHeart: 3,
};

const HASH_SWATCH_MIN: BrandSwatch = 5;
const HASH_SWATCH_MAX: BrandSwatch = 8;

/** Deterministic FNV-1a-style hash, kept small and dependency-free since it
 *  only needs to fan a brand name out across 4 buckets, not resist attack. */
function hashString(value: string): number {
  let hash = 2166136261;
  for (let i = 0; i < value.length; i++) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

export function brandSwatch(brand: string): BrandSwatch {
  const trimmed = brand.trim();
  if (!trimmed) return 4;
  const known = BRAND_SWATCHES[trimmed];
  if (known) return known;
  const range = HASH_SWATCH_MAX - HASH_SWATCH_MIN + 1;
  return (HASH_SWATCH_MIN + (hashString(trimmed) % range)) as BrandSwatch;
}

/** Static class strings (not template-built) so Tailwind's content scanner
 *  sees every `brand-swatch-N` utility actually in use. */
const BRAND_SWATCH_CLASSES: Record<BrandSwatch, { text: string; bg: string; dot: string }> = {
  1: { text: "text-brand-swatch-1", bg: "bg-brand-swatch-1/15", dot: "bg-brand-swatch-1" },
  2: { text: "text-brand-swatch-2", bg: "bg-brand-swatch-2/15", dot: "bg-brand-swatch-2" },
  3: { text: "text-brand-swatch-3", bg: "bg-brand-swatch-3/15", dot: "bg-brand-swatch-3" },
  4: { text: "text-brand-swatch-4", bg: "bg-brand-swatch-4/15", dot: "bg-brand-swatch-4" },
  5: { text: "text-brand-swatch-5", bg: "bg-brand-swatch-5/15", dot: "bg-brand-swatch-5" },
  6: { text: "text-brand-swatch-6", bg: "bg-brand-swatch-6/15", dot: "bg-brand-swatch-6" },
  7: { text: "text-brand-swatch-7", bg: "bg-brand-swatch-7/15", dot: "bg-brand-swatch-7" },
  8: { text: "text-brand-swatch-8", bg: "bg-brand-swatch-8/15", dot: "bg-brand-swatch-8" },
};

export function brandSwatchClasses(n: BrandSwatch): { text: string; bg: string; dot: string } {
  return BRAND_SWATCH_CLASSES[n];
}

/** Adora store ids observed in the seeded registry are 5 alphanumerics
 *  (spec 2.1); a blank value or the literal "NOID" both mean "not on Adora
 *  yet" and keep `available` false via the generated column. */
export const STORE_ID_PATTERN = /^[A-Za-z0-9]{5}$/;

export function isPlaceholderStoreId(storeId: string): boolean {
  const t = storeId.trim();
  return t === "" || t.toUpperCase() === "NOID";
}

/**
 * Canonical form for persistence: trimmed, and any placeholder spelling
 * ("", "noid", "Noid") collapsed to the literal NOID that the generated
 * `available` column and the partial unique index recognise.
 */
export function normalizeStoreId(storeId: string): string {
  const t = storeId.trim();
  return isPlaceholderStoreId(t) ? "NOID" : t;
}

/** Returns a user-facing error, or null when the id is acceptable. */
export function validateStoreId(storeId: string): string | null {
  if (isPlaceholderStoreId(storeId)) return null;
  return STORE_ID_PATTERN.test(storeId.trim())
    ? null
    : "Store ID must be 5 letters/numbers (or blank if it's not on Adora yet).";
}

/** Returns a user-facing error, or null when the value is acceptable. */
export function validateRequiredField(value: string, label: string): string | null {
  return value.trim() ? null : `${label} is required.`;
}

export const DUPLICATE_LOCATION_MESSAGE = "A store with this brand and location already exists.";

/** Case-insensitive (brand, location_name) lookup: the table has no
 *  uniqueness on names, so AddStoreDialog refuses a duplicate before insert and
 *  EditStoreDialog refuses a rename onto another store's pair (excluding the
 *  row being edited). */
export function findDuplicateStore(
  stores: readonly PosStore[],
  brand: string,
  locationName: string,
): PosStore | undefined {
  const b = brand.trim().toLowerCase();
  const l = locationName.trim().toLowerCase();
  return stores.find(
    (s) => s.brand.trim().toLowerCase() === b && s.location_name.trim().toLowerCase() === l,
  );
}
